/** Weergavelogica voor bekendmakingen, los van React zodat hij te testen is. */

type WithPermitFlag = { vergunning: boolean };

/**
 * Twee lijsten, want het zijn twee soorten nieuws: beleid zegt wat er mag gaan
 * gelden, een vergunning zegt wat er werkelijk gebeurt. Door elkaar gezet
 * verliest het beleid het altijd — dat hangt aan een gemeente en de vergunning
 * aan een adres, dus op afstand staat de dakkapel bovenaan.
 *
 * De vlag komt van de monitor: welke soorten een vergunning zijn hoort bij de
 * bron, niet bij de frontend.
 */
export const splitPolicyAndPermits = <T extends WithPermitFlag>(publications: T[]) => ({
  policy: publications.filter((p) => !p.vergunning),
  permits: publications.filter((p) => p.vergunning),
});

/** "12 september 2026" — bekendmakingen worden op datum besproken. */
export const formatDate = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleDateString("nl-NL", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "";

/** "13 mrt 2026" — voor de datumkolom van de tabel. */
export const formatDateShort = (iso: string | null) =>
  iso
    ? new Date(iso)
        .toLocaleDateString("nl-NL", { day: "numeric", month: "short", year: "numeric" })
        .replace(".", "")
    : "";

export type Outcome = "pending" | "positive" | "negative" | "notification";

// Sleutels zijn de `PROCEDURESTATUSSEN` van de monitor
const OUTCOMES: Record<string, Outcome> = {
  aanvraag: "pending",
  termijn_verlengd: "pending",
  ontwerp: "pending",
  verleend: "positive",
  vastgesteld: "positive",
  geweigerd: "negative",
  ingetrokken: "negative",
  melding: "notification",
};

/**
 * De stand van een procedure, teruggebracht tot wat een icoon kan zeggen:
 * loopt nog, pakte goed uit, of niet — of een melding, die geen uitkomst
 * kent. De status komt van de monitor (`procedurestatus`, omgevingsmonitor#86);
 * `onbekend`, een ontbrekend veld of een waarde die we niet kennen geeft geen
 * icoon.
 */
export const outcome = (procedureStatus?: string): Outcome | null =>
  (procedureStatus && OUTCOMES[procedureStatus]) || null;

/**
 * De typekolom: de activiteiten die de monitor in de titel herkende ("kap,
 * nieuwbouw"), in zijn volgorde; zonder activiteiten de soort van de bron
 * ("omgevingsvergunning", "participatie"). Classificatie hoort bij de monitor,
 * dus hier wordt niets uit de titel afgeleid.
 */
export const typeLabel = (p: { soort: string; activiteiten?: string[] }) =>
  p.activiteiten?.length ? p.activiteiten.join(", ") : p.soort;

const ON_PARCEL_METERS = 25;

/**
 * Afstand pas noemen als hij iets toevoegt: "op het perceel" is informatief,
 * "op 3 meter" suggereert een precisie die de bron niet heeft — het is het
 * gepubliceerde gebied, niet de exacte plek van de ingreep.
 */
export const formatDistance = (meters: number | null) => {
  if (meters === null) return "";
  if (meters < ON_PARCEL_METERS) return "op het perceel";
  if (meters < 1000) return `${Math.round(meters / 10) * 10} m`;
  return `${(meters / 1000).toFixed(1)} km`;
};

/** Dezelfde afstand voor de smalle tabelkolom: op het perceel zelf een streepje. */
export const formatDistanceShort = (meters: number | null) =>
  meters !== null && meters < ON_PARCEL_METERS ? "-" : formatDistance(meters);

type Matchable = {
  titel: string;
  gemeente: string;
  gepubliceerdOp: string | null;
  procedurestatus?: string;
};

/**
 * Woorden die niets over de zaak zeggen: de stand van de procedure ("Aanvraag
 * vergunning voor …" tegenover "Toestemming voor …") en lidwoorden en
 * voorzetsels. Wat overblijft is de zaak zelf: activiteit en adres.
 */
export const IGNORED_TITLE_WORDS = new Set(
  (
    "aanvraag aanvragen ingekomen ontvangen nieuwe verleende verleend toestemming " +
    "vergunning omgevingsvergunning besluit kennisgeving treedt niet direct werking " +
    "voor het de een van en te in op aan met bij is ter hoogte"
  ).split(" ")
);

/**
 * Vanaf deze overlap (Jaccard over de titelwoorden) is het dezelfde zaak.
 * Gemeten op echte paren:
 * dezelfde zaak 0,69–1,00, een andere zaak op dezelfde dag en plek ≤ 0,29.
 */
export const SAME_CASE_OVERLAP = 0.6;

/**
 * Laat een aanvraag weg als zijn besluit in dezelfde lijst staat: een
 * publicatie met `procedurestatus` "aanvraag" valt weg als er een "verleend"
 * is met dezelfde datum en gemeente en een titel die voor minstens
 * `SAME_CASE_OVERLAP` overeenkomt. De monitor koppelt aanvraag en besluit niet;
 * dit is een heuristiek, en bij twijfel blijft de aanvraag staan.
 */
export const withoutAnsweredApplications = <T extends Matchable>(publications: T[]): T[] => {
  const decisions = publications.filter((p) => p.procedurestatus === "verleend");
  const answered = (application: T) =>
    decisions.some(
      (d) =>
        d.gepubliceerdOp === application.gepubliceerdOp &&
        d.gemeente === application.gemeente &&
        titleOverlap(d.titel, application.titel) >= SAME_CASE_OVERLAP
    );
  return publications.filter((p) => p.procedurestatus !== "aanvraag" || !answered(p));
};

const titleWords = (title: string) =>
  new Set(
    (title.toLowerCase().match(/[a-zà-ÿ0-9]+/g) ?? []).filter(
      (w) => w.length > 2 && !IGNORED_TITLE_WORDS.has(w)
    )
  );

/** Jaccard: de gedeelde woorden gedeeld door alle woorden van beide titels. */
const titleOverlap = (a: string, b: string) => {
  const A = titleWords(a);
  const B = titleWords(b);
  const shared = [...A].filter((w) => B.has(w)).length;
  const all = A.size + B.size - shared;
  return all ? shared / all : 0;
};
